import React from 'react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';

export default function CustomerSimulator() {
  const { selectedSite } = useSite();
  const [count, setCount] = React.useState(25);
  const [concurrent, setConcurrent] = React.useState(5);
  const [headless, setHeadless] = React.useState(true);
  const [runs, setRuns] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [logsFor, setLogsFor] = React.useState<string | null>(null);
  const [logs, setLogs] = React.useState<string[]>([]);
  const [logsLoading, setLogsLoading] = React.useState(false);

  const siteId = selectedSite?.id || selectedSite?._id;

  const refresh = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const r = await authFetch(`/simulator/runs?siteId=${siteId}&limit=10`);
      if (!r.ok) {
        setRuns([]);
        return;
      }
      const data = await r.json();
      setRuns(Array.isArray(data) ? data : []);
    } catch (e) {
      setRuns([]);
    }
  }, [siteId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const start = async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const res = await authFetch('/simulator/runs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId, customerCount: count, options: { concurrentBrowsers: concurrent, headless } })
      });
      if (!res.ok) throw new Error('Failed to start');
      await refresh();
    } finally {
      setLoading(false);
    }
  };

  const cancel = async (runId: string) => {
    await authFetch(`/simulator/runs/${runId}`, { method: 'DELETE' });
    await refresh();
  };

  const openLogs = async (runId: string) => {
    setLogsFor(runId);
    setLogsLoading(true);
    try {
      const r = await authFetch(`/simulator/runs/${runId}/logs?tail=400`);
      const data = await r.json();
      setLogs(Array.isArray(data.logs) ? data.logs : []);
    } finally {
      setLogsLoading(false);
    }
  };
  const closeLogs = () => { setLogsFor(null); setLogs([]); };
  const refreshLogs = async () => { if (logsFor) openLogs(logsFor); };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Customer Simulator</h1>
      <div className="bg-card p-4 rounded-lg space-y-4">
        <div className="flex gap-4 items-center flex-wrap">
          <div>
            <div className="text-sm text-muted-foreground">Selected Site</div>
            <div className="font-medium">{selectedSite?.name || 'None'}</div>
          </div>
          <div>
            <label className="text-sm">Customers</label>
            <input type="number" min={1} max={500} value={count} onChange={e=>setCount(parseInt(e.target.value||'1'))} className="ml-2 border rounded px-2 py-1 w-24 bg-background text-foreground" />
          </div>
          <div>
            <label className="text-sm">Concurrent</label>
            <input type="number" min={1} max={10} value={concurrent} onChange={e=>setConcurrent(parseInt(e.target.value||'1'))} className="ml-2 border rounded px-2 py-1 w-20 bg-background text-foreground" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={headless} onChange={e=>setHeadless(e.target.checked)} /> Headless
          </label>
          <button onClick={start} disabled={!siteId || loading} className="px-4 py-2 bg-primary text-primary-foreground rounded disabled:opacity-50">
            {loading ? 'Starting…' : 'Start Simulation'}
          </button>
        </div>
      </div>
      <div className="bg-card p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold">Recent Runs</h2>
          <button onClick={refresh} className="text-sm px-2 py-1 border rounded hover:bg-accent">Refresh</button>
        </div>
        <div className="divide-y">
          {Array.isArray(runs) && runs.map((r)=> (
            <div key={r.id} className="py-2 flex items-center justify-between">
              <div className="space-x-4 text-sm">
                <span className="font-mono">{r.id.slice(0,8)}</span>
                <span>{r.status}</span>
                <span>{r.customer_count} users</span>
                <span>{new Date(r.created_at).toLocaleString()}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={()=>openLogs(r.id)} className="text-sm px-2 py-1 border rounded hover:bg-accent">View logs</button>
                {(r.status === 'starting' || r.status === 'pending' || r.status === 'running') && (
                  <button onClick={()=>cancel(r.id)} className="text-sm px-2 py-1 border rounded hover:bg-accent">Cancel</button>
                )}
              </div>
            </div>
          ))}
          {(!Array.isArray(runs) || runs.length === 0) && <div className="text-sm text-muted-foreground">No runs yet.</div>}
        </div>
      </div>

      {logsFor && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg w-[800px] max-h-[80vh] flex flex-col">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="font-semibold">Logs: {logsFor.slice(0,8)}</div>
              <div className="flex gap-2">
                <button onClick={refreshLogs} className="text-sm px-2 py-1 border rounded hover:bg-accent">Refresh</button>
                <button onClick={closeLogs} className="text-sm px-2 py-1 border rounded hover:bg-accent">Close</button>
              </div>
            </div>
            <div className="p-3 overflow-auto text-xs font-mono whitespace-pre-wrap bg-muted/50 text-foreground">
              {logsLoading ? 'Loading…' : (logs.length ? logs.join('\n') : 'No logs yet.')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
